import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getRestaurantMenu } from '../api/client';
import { getTemplateComponent } from '../templates/TemplateRegistry';
import { normalizeMenuData } from '../utils/normalizeMenuData';
import './TemplateThumbnailPage.css';

const getQuery = (search) => {
    const params = new URLSearchParams(search);
    return {
        templateName: params.get('templateName') || 'Classic Elegance',
        shortcode: params.get('shortcode') || 'test',
        kind: params.get('kind') || 'qr',
    };
};

const TemplateThumbnailPage = () => {
    const location = useLocation();
    const { templateName, shortcode, kind } = useMemo(() => getQuery(location.search), [location.search]);

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        document.body.classList.add('thumbnail-mode');
        return () => document.body.classList.remove('thumbnail-mode');
    }, []);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                setError(null);

                const menuData = await getRestaurantMenu(shortcode);
                let normalizedData = menuData;

                if (normalizedData && !normalizedData.menus && (normalizedData.id || normalizedData.name)) {
                    normalizedData = { restaurant: normalizedData, menus: [] };
                }

                if (normalizedData) {
                    try {
                        normalizedData = normalizeMenuData(normalizedData);
                    } catch {
                        // ignore normalization issues for thumbnails
                    }
                }

                if (!cancelled) {
                    setData(normalizedData);
                }
            } catch {
                if (!cancelled) {
                    setError('Unable to load thumbnail data');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [shortcode]);

    const TemplateComponent = useMemo(() => getTemplateComponent(templateName), [templateName]);

    const state = loading ? 'loading' : (error ? 'error' : 'ready');

    return (
        <div className="thumb-page" data-kind={kind}>
            {/* Marker used by the screenshot script to know the UI state */}
            <div
                id="thumb-ready"
                data-state={state}
                style={{ display: 'none' }}
            />

            {loading && <div className="thumb-status">Loading...</div>}
            {!loading && error && <div className="thumb-status">{error}</div>}

            {!loading && !error && (
                <div id="thumb-shot" className={`thumb-shot ${kind === 'qr' ? 'thumb-shot-qr' : 'thumb-shot-generic'}`}>
                    {kind === 'qr' ? (
                        <div className="thumb-qr-composite">
                            <div className="thumb-bg" aria-hidden="true">
                                <div className="thumb-bg-inner">
                                    <TemplateComponent data={data} />
                                </div>
                            </div>

                            <div className="thumb-phone" aria-label="Phone frame">
                                <div className="thumb-phone-frame">
                                    <div className="thumb-phone-notch" aria-hidden="true" />
                                    <div className="thumb-screen">
                                        <div className="thumb-screen-content">
                                            <TemplateComponent data={data} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div id="thumb-capture" className="thumb-capture">
                            <TemplateComponent data={data} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TemplateThumbnailPage;
